import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller, { inject as controller } from '@ember/controller';
import {
  DEFAULT_CAMPAIGN_OVERVIEW_TAB_GROUPS,
  API_TRIGGERED_BROADCAST_OVERVIEW_TAB_GROUPS,
  WEBHOOK_CAMPAIGN_OVERVIEW_TAB_GROUPS,
} from 'cio/utils/campaign-navigation';
import { task } from 'ember-concurrency';

import RouterService from '@ember/routing/router-service';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import CampaignModel, { CampaignStateActions, CampaignType, CampaignUpdateTypes } from 'cio/models/campaign';
import MasqService from 'cio/services/masq';
import ApiErrorsService from 'cio/services/api-errors';
import AlertsService from 'cio/services/alerts';
import MetricsService from 'cio/services/metrics';
import EnvironmentService from 'cio/services/environment';
import { Entities, Periods } from 'cio/utils/metrics/common';
import AppController from 'cio/pods/app/controller';
import FeaturesService from 'ember-feature-flags/services/features';
import { tracked } from '@glimmer/tracking';
import AuthService from 'cio/services/auth';

const CAMPAIGN_OVERVIEW_TAB_GROUPS: Partial<Record<CampaignType, any>> = {
  [CampaignType.APITriggered]: API_TRIGGERED_BROADCAST_OVERVIEW_TAB_GROUPS,
  [CampaignType.Webhook]: WEBHOOK_CAMPAIGN_OVERVIEW_TAB_GROUPS,
};

export default class OverviewController extends Controller {
  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service masq!: MasqService;
  @service metrics!: MetricsService;
  @service router!: RouterService;
  @service features!: FeaturesService;

  declare model: CampaignModel;

  @controller('app') appController!: AppController;

  queryParams = ['unfollow'];
  @tracked unfollow: string | true | null = null;
  @tracked metric!: Promise<any>;

  CampaignStateActions = CampaignStateActions;

  get unfollowFromQueryParam() {
    return this.unfollow === true || this.unfollow === 'true';
  }

  @computed('model.type')
  get tabGroups() {
    return CAMPAIGN_OVERVIEW_TAB_GROUPS[this.model.type] ?? DEFAULT_CAMPAIGN_OVERVIEW_TAB_GROUPS;
  }

  @computed('router.currentRouteName')
  get isWorkflowRoute() {
    return this.router.currentRouteName.includes('workflow.actions');
  }

  @computed('model.type')
  get _modelRoute() {
    return this.model.isApiTriggered ? 'broadcasts.list.api-triggered' : 'campaigns';
  }

  get hasReadOnlyPermission() {
    if (this.model.isApiTriggered) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  _saveCampaignDetails = task({ drop: true }, async (attr: keyof CampaignModel, value: any) => {
    this.model.set(attr, value);
    this.model.set('updateType', CampaignUpdateTypes.Main);

    return this.model.save();
  });

  getMetricTask = task({ drop: true }, async () => {
    this.metric = await this.metrics.fetchMetric(Entities.CAMPAIGN, this.model.id, Periods.DAYS, null, 'email', {
      campaignId: this.model.id,
      environmentId: this.environment.current.id,
      version: 2,
    });
  });

  @action
  resetCampaign() {
    this.model.rollbackAttributes();
  }

  @action
  afterDeleteCampaign() {
    this.router.transitionTo(this.model.isApiTriggered ? 'broadcasts.list.api-triggered' : 'campaigns');
  }
}
